import { useEffect, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AcDialog, Button } from "@cntrl/ui";
import { onOAuthComplete, openOAuthPopup, popupReturnPath, SETTINGS_PARAM } from "../../app/oauthPopup.js";
import { pushToast } from "../../app/toasts.js";
import {
  GOOGLE_CALENDAR_KEY,
  useDisconnectGoogleCalendar,
  useGoogleCalendar,
  useStartGoogleCalendar,
} from "../../app/hooks/useGoogleCalendar.js";
import styles from "./SettingsScreen.module.scss";

/** The OAuth `connected=` value the callback reports for this connect. */
const PROVIDER = "google_calendar";

/** Where a popup-blocked connect lands: back on this page, with Settings reopened on Account. */
function fallbackReturnPath(): string {
  const url = new URL(window.location.href);
  url.searchParams.set(SETTINGS_PARAM, "account");
  return `${url.pathname}${url.search}`;
}

/**
 * Google Calendar, PER PERSON (2026-10-09). The confirmed shows you are on — your own band's if you own it, plus
 * any show whose member list names you — on a calendar of their own, "Artist CNTRL", in Google's default color.
 *
 * It lives on the Account tab, in both shells, because it is about YOU and not about a band: a crew member with no
 * band-admin rights and a manager in the roster shell both reach it from their own account menu. (The band-wide
 * ICS feed sits just below it as Show Sync — a different job: the band's shows, not yours.)
 *
 * The scope is `calendar.app.created`, so the copy can say the true and narrow thing: we make one calendar and cannot
 * see anything else in the account.
 */
export function GoogleCalendarSection() {
  const link = useGoogleCalendar();
  const start = useStartGoogleCalendar();
  const disconnect = useDisconnectGoogleCalendar();
  const qc = useQueryClient();
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  // The popup reports back here. Filter on our provider: the connections tab listens on the same channel.
  useEffect(
    () =>
      onOAuthComplete(({ provider, ok }) => {
        if (provider !== PROVIDER) return;
        pushToast(ok ? "Google Calendar connected." : "Couldn't connect Google Calendar.", ok ? "info" : "error");
        void qc.invalidateQueries({ queryKey: GOOGLE_CALENDAR_KEY });
      }),
    [qc],
  );

  // No answer yet, or no answer at all: draw nothing rather than a heading over a spinner on a tab of rows.
  if (!link.data) return null;
  const { available, state, calendarReady } = link.data;

  const connect = () => {
    // Open the popup SYNCHRONOUSLY, before any await: a window.open after the URL comes back has lost its
    // user-gesture provenance and every major browser blocks it.
    const popup = openOAuthPopup();
    void start
      .mutateAsync({ returnUrl: popup ? popupReturnPath() : fallbackReturnPath() })
      .then((res) => {
        if (!res.authorizeUrl) {
          popup?.close();
          return;
        }
        if (popup) popup.location.href = res.authorizeUrl;
        else window.location.assign(res.authorizeUrl);   // popup blocked — the full-page hop
      })
      .catch(() => popup?.close());   // the hook already toasted the reason
  };

  let description: string;
  let actions: ReactNode;
  if (!available) {
    description = "Not available on this environment yet.";
    actions = null;
  } else if (state === "error") {
    description =
      "Google Calendar lost access. Reconnect to keep your shows updated.";
    actions = (
      <>
        <Button variant="ghost" size="sm" onClick={() => setConfirmingDisconnect(true)}>
          Disconnect
        </Button>
        <Button variant="accent" size="sm" loading={start.isPending} onClick={connect}>
          Reconnect
        </Button>
      </>
    );
  } else if (state === "connected") {
    description = calendarReady
      ? "Your shows are on the Artist CNTRL calendar."
      : "Setting up your calendar…";
    actions = (
      <Button variant="ghost" size="sm" onClick={() => setConfirmingDisconnect(true)}>
        Disconnect
      </Button>
    );
  } else {
    description =
      "Add your confirmed shows to a separate Artist CNTRL calendar. We can't see anything else in your Google account.";
    actions = (
      <Button variant="accent" size="sm" loading={start.isPending} onClick={connect}>
        Connect Google Calendar
      </Button>
    );
  }

  return (
    <>
      <div className={`${styles.toggleRow} ${styles.calendarRow}`}>
        <span className={styles.toggleText}>
          <span className={styles.toggleLabel}>Google Calendar</span>
          <span className={styles.toggleDesc}>{description}</span>
        </span>
        {actions && <span className={styles.calendarActions}>{actions}</span>}
      </div>

      {confirmingDisconnect && (
        <AcDialog
          title="Disconnect Google Calendar"
          message="This deletes the Artist CNTRL calendar from Google. Your other calendars stay as they are."
          confirmLabel="Disconnect"
          tone="danger"
          loading={disconnect.isPending}
          onConfirm={() => disconnect.mutate(undefined, { onSettled: () => setConfirmingDisconnect(false) })}
          onCancel={() => setConfirmingDisconnect(false)}
        />
      )}
    </>
  );
}
